/**
 * Types, constants, and defaults for the RFQ Create Wizard.
 *
 * CR-5466 wizard rebuild: BasicDetailsForm matches the target design
 * (Reference, RFQ Type, RFQ Date, RFQ Deadline, NDA, Description).
 * Legacy fields (title, category, priority, currency, contract_type,
 * validity_period_days, terms, special_requirements, auto_create_contract)
 * are no longer collected here — the backend keeps reasonable defaults so
 * historical workflows that still reference them continue to work.
 */
import type {
  RFQCostType,
  RFQCertificateType,
} from '../../lib/api';

export interface LineItemForm {
  item_description: string;
  part_number: string;
  quantity: number;
  unit_of_measure: string;
  target_unit_price: number | null;
  // CR-5467: drawing_issue / revision number is required for version
  // control on engineering drawings; surfaced at line level.
  drawing_issue: string;
  // CR-5467: delivery_location and required_delivery_date no longer
  // belong on the line item — they're RFQ-header-level concerns.
  // Kept on the type as optional so existing draft / response payloads
  // don't fall over until the header form ships in the follow-up.
  delivery_location?: string;
  required_delivery_date?: string;
  technical_specifications: string;
  notes: string;
}

export interface AdditionalCostForm {
  cost_type: RFQCostType;
  description: string;
  amount: number;
  notes: string;
}

export interface RequiredCertificateForm {
  certificate_type: RFQCertificateType;
  certificate_name: string;
  description: string;
  is_mandatory: boolean;
  expiry_tolerance_days: number;
}

export interface SupplierOption {
  id: number;
  company: number;
  company_name: string;
}

/** CR-5466: form shape for the redesigned Basic Details step. */
export interface BasicDetailsForm {
  /** RFQ Description — the only large free-text on the screen */
  description: string;
  /** Customer-facing reference (e.g. internal PO/RFQ number) */
  reference: string;
  /** FK to RFQType row; null when no admin-managed types exist yet */
  rfq_type: number | null;
  /** RFQ Date — when the RFQ is being raised (ISO yyyy-mm-dd) */
  issue_date: string;
  /** RFQ Deadline — supplier response cut-off (ISO yyyy-mm-dd) */
  response_deadline: string;
  /** Non-Disclosure Agreement Required? */
  nda_required: boolean;
}

export interface WizardStep {
  id: number;
  title: string;
  description: string;
}

export const STEPS: WizardStep[] = [
  { id: 1, title: 'Basic Details', description: 'RFQ information and contract type' },
  { id: 2, title: 'Line Items', description: 'Parts and quantities required' },
  { id: 3, title: 'Additional Costs', description: 'Tooling, packaging, transport' },
  // Collects the required certificates and, when NDA is ticked in Basic
  // Details, the NDA file itself. The file is held in wizard state (not the
  // server-side draft — a File can't be serialised) and uploaded right after
  // the RFQ is created, before it is sent. Previously this step only told the
  // customer to "upload the NDA after creating the RFQ" and nothing ever
  // asked again, so RFQs went out with no NDA and suppliers were stuck.
  { id: 4, title: 'Certificates & NDA', description: 'Certificates suppliers must provide, and the NDA they must accept' },
  { id: 5, title: 'Suppliers', description: 'Select recipients' },
  { id: 6, title: 'Review & Send', description: 'Final review' },
];

export const EMPTY_LINE_ITEM: LineItemForm = {
  item_description: '',
  part_number: '',
  quantity: 1,
  unit_of_measure: 'Each',
  target_unit_price: null,
  drawing_issue: '',
  technical_specifications: '',
  notes: '',
};

export const EMPTY_COST: AdditionalCostForm = {
  cost_type: 'tooling',
  description: '',
  amount: 0,
  notes: '',
};

export const EMPTY_CERTIFICATE: RequiredCertificateForm = {
  certificate_type: 'iso_9001',
  certificate_name: '',
  description: '',
  is_mandatory: true,
  expiry_tolerance_days: 90,
};

export const DEFAULT_BASIC_DETAILS: BasicDetailsForm = {
  description: '',
  reference: '',
  rfq_type: null,
  issue_date: '',
  response_deadline: '',
  nda_required: false,
};
